import { ArpMedia } from '@arpeggi/shared'
import { FC } from 'react'
import { queryClient } from '../../../../features/react-query-provider'
import { useFeaturedMediaAPI } from '../../../api/v2/FeaturedMediaAPI'
import { useSnackBar } from '../../../components/internal/hooks/useSnackBar'
import Button from '../../core/Button/Button'
import { IconButton } from '../../core/Components'
import { Flex } from '../../core/Flex'
import * as Dropdown from '../../platform/new-explore/components/Dropdown'

interface Props {
  selectedArpMedia: ArpMedia[]
  isDisabled?: boolean
  onClearSelection: () => void
  onBulkActionStart: () => void
  onBulkActionEnd: () => void
}

export const BulkActions: FC<Props> = ({
  selectedArpMedia,
  isDisabled,
  onClearSelection,
  onBulkActionStart,
  onBulkActionEnd
}) => {
  const { createFeaturedMedia, deleteFeaturedMedia } = useFeaturedMediaAPI()
  const { dispatchSnack } = useSnackBar()

  const handleBulkAction = async (callback: () => Promise<void>) => {
    onBulkActionStart()
    await callback()
    await queryClient.invalidateQueries(['arp-media'])
    onBulkActionEnd()
  }

  const handleAddToFeaturedMedia = async () => {
    handleBulkAction(async () => {
      console.log(selectedArpMedia)

      const nonFeaturedSelection = selectedArpMedia.filter((x) => x.isFeatured !== true)
      await Promise.all(
        nonFeaturedSelection.map((nonFeatured) => createFeaturedMedia(nonFeatured.id))
      )
      dispatchSnack('Selection added to featured media!')
    })
  }

  const handleDeleteFromFeaturedMedia = () => {
    handleBulkAction(async () => {
      const featuredSelection = selectedArpMedia.filter((x) => x.isFeatured !== false)
      await Promise.all(featuredSelection.map((nonFeatured) => deleteFeaturedMedia(nonFeatured.id)))
      dispatchSnack('Selection removed from featured media!')
    })
  }

  return (
    <Flex css={{ gap: '8px' }}>
      <Button big type="outlined" onClick={onClearSelection} disabled={isDisabled}>
        Clear selection
      </Button>
      <Dropdown.Root>
        <Dropdown.Trigger
          asChild
          css={{
            border: 'none',
            boxShadow: 'none',
            padding: 0,
            '&:hover': { borderColor: 'none' }
          }}
          disabled={isDisabled}
        >
          <IconButton>
            <Button
              big
              type="dark"
              as="div"
              noHover={isDisabled}
              css={{ opacity: isDisabled ? 0.4 : 1 }}
            >
              Bulk actions
            </Button>
          </IconButton>
        </Dropdown.Trigger>
        <Dropdown.Portal>
          <Dropdown.Content side="bottom" sideOffset={4}>
            <Dropdown.Item
              onClick={(e) => {
                e.stopPropagation()
                handleAddToFeaturedMedia()
              }}
            >
              Add to featured media
            </Dropdown.Item>
            <Dropdown.Item
              onClick={(e) => {
                e.stopPropagation()
                handleDeleteFromFeaturedMedia()
              }}
            >
              Remove from featured media
            </Dropdown.Item>
          </Dropdown.Content>
        </Dropdown.Portal>
      </Dropdown.Root>
    </Flex>
  )
}
